import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Mail,
    Phone,
    MapPin,
    Instagram,
    Facebook,
    Linkedin,
    ChevronRight
} from 'lucide-react';
import { API } from '../config';
import logo from '../images/FINAL-DESIGN-LOGO-01.png';

const Footer = () => {
    const [settings, setSettings] = useState({
        tagline: 'Leading the Future of Integrated Poultry Farming',
        description: 'SR Agro Farms is a premier integrated poultry enterprise committed to excellence, sustainability, and empowering local farming communities across Karnataka.',
        email: 'info@sragrofarms.com',
        phone: '+91 92427 92427',
        location: 'No. 12, 4th Cross, 5th Main, Bangalore - 560001',
        copyright: '© 2026 SR Agro Farms. All rights reserved.',
        facebook: '#',
        instagram: '#',
        linkedin: '#'
    });

    const [seoContent, setSeoContent] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    const handleQuickLinkClick = (link) => {
        if (link.path.startsWith('/#')) {
            if (location.pathname !== '/') {
                navigate('/', { state: { scrollTo: link.path.replace('/', '') } });
            } else {
                const id = link.path.replace('/#', '');
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }
        } else {
            navigate(link.path);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [footerRes, seoRes] = await Promise.all([
                    axios.get(`${API}/footer`),
                    axios.get(`${API}/site-content`)
                ]);
                if (footerRes.data) setSettings(prev => ({ ...prev, ...footerRes.data }));
                const seo = seoRes.data?.find(c => c.section_name === 'footer_seo');
                if (seo) setSeoContent(seo);
            } catch (error) {
                console.error('Failed to fetch footer data:', error);
            }
        };
        fetchData();
    }, []);

    const quickLinks = [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/#about' },
        { name: 'Our Impact', path: '/#impact' },
        { name: 'Why Choose Us', path: '/#why-choose' },
        { name: 'Our Services', path: '/#services' },
        { name: 'Contact', path: '/#contact' }
    ];

    return (
        <footer className="bg-[#05110d] text-white pt-32 pb-12 overflow-hidden relative">
            {/* Background Decorative Patterns */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-agro-lime/40 to-transparent"></div>
            <div className="absolute -top-24 -left-24 w-[500px] h-[500px] bg-agro-lime/5 rounded-full blur-[120px]"></div>
            <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] bg-agro-lime/5 rounded-full blur-[120px]"></div>

            <div className="container mx-auto px-6 relative z-10">
                {/* Main Footer Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-20 mb-32">

                    {/* Brand Section */}
                    <div className="lg:col-span-5">
                        <Link to="/" className="inline-block mb-10 group">
                            <div className="flex items-center gap-5">
                                <div className="bg-white p-2.5 rounded-[1.25rem] shadow-[0_20px_40px_rgba(0,0,0,0.3)] transform group-hover:rotate-6 transition-all duration-500">
                                    <img src={logo} alt="SR Agro Farms" className="h-14 w-auto" />
                                </div>
                                <div className="flex flex-row items-baseline gap-2 flex-nowrap">
                                    <span className="font-serif text-2xl md:text-3xl font-bold leading-tight tracking-tight whitespace-nowrap">SR AGRO</span>
                                    <span className="font-sans text-2xl md:text-3xl uppercase tracking-widest text-white font-bold whitespace-nowrap">FARMS</span>
                                </div>
                            </div>
                        </Link>
                        <p className="text-gray-400 text-xl mb-10 leading-relaxed font-light max-w-md">
                            {settings.description}
                        </p>
                        <div className="flex gap-5">
                            {[
                                { icon: Instagram, href: settings.instagram, label: 'Instagram' },
                                { icon: Facebook, href: settings.facebook, label: 'Facebook' },
                                { icon: Linkedin, href: settings.linkedin, label: 'LinkedIn' }
                            ].map((social, i) => (
                                <a
                                    key={i}
                                    href={social.href}
                                    aria-label={social.label}
                                    className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-agro-lime hover:text-agro-green-dark transition-all duration-500 transform hover:-translate-y-2 hover:shadow-[0_15px_30px_rgba(206,255,26,0.15)] group/social"
                                >
                                    <social.icon size={24} className="group-hover/social:scale-110 transition-transform" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="lg:col-span-3">
                        <h4 className="text-agro-lime font-sans uppercase tracking-[0.3em] text-xs font-black mb-10">Exploration</h4>
                        <ul className="space-y-6">
                            {quickLinks.map((link) => (
                                <li key={link.name}>
                                    <button
                                        onClick={() => handleQuickLinkClick(link)}
                                        className="text-gray-400 hover:text-white transition-all flex items-center group text-base font-light tracking-wide"
                                    >
                                        <span className="w-2 h-2 rounded-full bg-agro-lime mr-4 opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300"></span>
                                        {link.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div className="lg:col-span-4">
                        <h4 className="text-agro-lime font-sans uppercase tracking-[0.3em] text-xs font-black mb-10">Connection</h4>
                        <div className="space-y-10">
                            <div className="flex items-start gap-6 group cursor-pointer" onClick={() => window.open(`https://maps.google.com/?q=${settings.location}`, '_blank')}>
                                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-agro-lime group-hover:bg-agro-lime group-hover:text-agro-green-dark transition-all duration-700 group-hover:rotate-[360deg] shrink-0 shadow-lg">
                                    <MapPin size={24} />
                                </div>
                                <div className="pt-1">
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-black mb-2">Presence</p>
                                    <p className="text-gray-300 leading-relaxed font-light text-lg group-hover:text-white transition-colors">{settings.location}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-6 group cursor-pointer" onClick={() => window.location.href = `tel:${settings.phone}`}>
                                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-agro-lime group-hover:bg-agro-lime group-hover:text-agro-green-dark transition-all duration-500 group-hover:scale-110 shrink-0 shadow-lg">
                                    <Phone size={24} />
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-black mb-1">Direct Line</p>
                                    <p className="text-gray-300 font-serif text-2xl font-bold group-hover:text-white transition-colors tracking-tight">{settings.phone}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-6 group cursor-pointer" onClick={() => window.location.href = `mailto:${settings.email}`}>
                                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-agro-lime group-hover:bg-agro-lime group-hover:text-agro-green-dark transition-all duration-500 group-hover:scale-110 shrink-0 shadow-lg">
                                    <Mail size={24} />
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-black mb-1">Inquiry</p>
                                    <p className="text-gray-300 text-lg font-light group-hover:text-white transition-colors tracking-wide">{settings.email}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SEO Content Block (Dynamic) */}
                {seoContent && (
                    <div className="py-20 border-t border-white/5 mb-20 bg-white/[0.02] -mx-6 px-6 rounded-[3rem]">
                        <h5 className="text-agro-lime font-serif text-2xl mb-8 flex items-center gap-4">
                            <span className="w-12 h-px bg-agro-lime/30"></span>
                            {seoContent.title}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-gray-400 text-base leading-relaxed max-w-6xl font-light">
                            {seoContent.content.split('\n').filter(p => p.trim()).map((para, i) => (
                                <p key={i}>{para}</p>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bottom Bar */}
                <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-10">
                    <div className="flex flex-col md:flex-row items-center gap-6 text-gray-500 text-[10px] font-black tracking-[0.3em] uppercase">
                        <span>{settings.copyright}</span>
                        <span className="hidden md:block w-1.5 h-1.5 rounded-full bg-white/10"></span>
                        <span className="text-agro-lime/50">Designed for Excellence</span>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex -space-x-3">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="w-10 h-10 rounded-full border-2 border-[#05110d] bg-white/5 backdrop-blur-md flex items-center justify-center overflow-hidden grayscale hover:grayscale-0 transition-all duration-500 shadow-xl">
                                    <img src={`/images/sr-agro-${i}.jpeg`} alt="Cert" className="w-full h-full object-cover opacity-60 hover:opacity-100 transition-opacity" />
                                </div>
                            ))}
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-black ml-4 border-l border-white/10 pl-4 py-1">Certified Excellence</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;

